<template>
  <f7-panel
    left
    resizable
    :backdrop="false"
    class="createGroupLeftPanel groupPanel"
    @panel:opened="onMembersPanelOpened"
    @panel:closed="onMembersPanelClosed"
  >
    <f7-view>
      <f7-page page-with-subnavbar class="groupPage">
        <f7-navbar no-shadow>
          <f7-nav-left>
            <f7-link panel-close class="groupIconBtn" :aria-label="$t('common.close')">
              <f7-icon ios="f7:multiply" aurora="f7:multiply" md="material:close"></f7-icon>
            </f7-link>
          </f7-nav-left>
          <f7-nav-title class="groupNavTitle">
            <div class="groupNavTitle__main">{{ flowTitle }}</div>
            <div v-if="isSubGroupCreation" class="groupNavTitle__sub">
              {{ parentGroupName }}
            </div>
          </f7-nav-title>
        </f7-navbar>
        <f7-subnavbar :inner="false" no-shadow class="groupSubnavbar">
          <f7-searchbar
            :placeholder="$t('tamtam.searchMembers')"
            search-container=".groupMemberList"
            search-in=".item-title, .item-after, .item-footer"
            :clear-button="true"
            :backdrop="false"
          ></f7-searchbar>
        </f7-subnavbar>

        <f7-block v-if="selectedMembers.length" strong class="groupSelected">
          <div class="groupSelected__label">
            {{ $t("tamtam.selectedMembers", { count: selectedMembers.length }) }}
          </div>
          <div class="groupChips">
            <f7-chip
              v-for="member in selectedMembers"
              :key="memberKey(member)"
              outline
              class="groupChip"
              :text="member.name"
              :media="initialFor(member)"
              deleteable
              @click="removeMember(member)"
            >
              <template #media>
                <img
                  v-if="avatarFor(member)"
                  class="groupChip__media"
                  :src="avatarFor(member)"
                  :alt="member.name"
                />
              </template>
            </f7-chip>
          </div>
        </f7-block>

        <div v-if="isLoadingCandidates" class="groupState">
          <div class="preloader"></div>
        </div>
        <template v-else>
          <f7-list
            dividers-ios
            strong-ios
            media-list
            class="searchbar-not-found no-margin"
          >
            <f7-list-item :title="$t('common.nothingFound')"></f7-list-item>
          </f7-list>
          <f7-list
            dividers-ios
            strong-ios
            media-list
            no-chevron
            no-margin
            class="groupMemberList searchbar-found"
          >
            <li v-for="member in candidates" :key="memberKey(member)">
              <label class="item-checkbox item-content groupMember">
                <input
                  type="checkbox"
                  name="group_user"
                  :value="memberKey(member)"
                  :checked="isSelected(member)"
                  @change="toggleMember(member)"
                />
                <i v-if="!f7.support.touch" class="icon icon-checkbox"></i>
                <div class="item-media">
                  <img
                    v-if="avatarFor(member)"
                    class="groupMember__media"
                    :src="avatarFor(member)"
                    :alt="member.name"
                  />
                  <f7-badge v-else color="auto" class="groupMember__badge">
                    {{ initialFor(member) }}
                  </f7-badge>
                </div>
                <div class="item-inner">
                  <div class="item-title">{{ member.name }}</div>
                  <div class="item-after">{{ member.phoneNumber }}</div>
                  <div v-if="member.tag" class="item-footer">{{ member.tag }}</div>
                </div>
              </label>
            </li>
          </f7-list>
        </template>
        <f7-toolbar tabbar bottom no-shadow class="groupFooter">
          <div class="page-content">
            <f7-button
              large
              fill
              class="groupActionBtn"
              :disabled="!canContinue"
              @click="goToDetails"
            >
              {{ $t("tamtam.continue") }}
            </f7-button>
          </div>
        </f7-toolbar>
      </f7-page>
    </f7-view>
  </f7-panel>

  <f7-panel
    left
    resizable
    :backdrop="false"
    class="saveGroupLeftPanel groupPanel"
    @panel:opened="onDetailsPanelOpened"
  >
    <f7-view>
      <f7-page page-with-subnavbar class="groupPage">
        <f7-navbar no-shadow>
          <f7-nav-left>
            <f7-link
              panel-open=".createGroupLeftPanel"
              class="groupIconBtn"
              :aria-label="$t('common.back')"
            >
              <f7-icon
                ios="f7:chevron_left"
                aurora="f7:chevron_left"
                md="material:chevron_left"
              ></f7-icon>
            </f7-link>
          </f7-nav-left>
          <f7-nav-title class="groupNavTitle">
            <div class="groupNavTitle__main">{{ flowTitle }}</div>
            <div v-if="isSubGroupCreation" class="groupNavTitle__sub">
              {{ parentGroupName }}
            </div>
          </f7-nav-title>
          <f7-nav-right>
            <f7-link panel-close class="groupTextBtn" :aria-label="$t('common.close')">
              {{ $t("common.close") }}
            </f7-link>
          </f7-nav-right>
        </f7-navbar>

        <f7-list dividers-ios strong-ios media-list no-margin class="groupAvatarList">
          <f7-list-item link="#" @click="pickAvatar">
            <template #media>
              <img
                v-if="avatarPreview"
                class="groupAvatar__media"
                :src="avatarPreview"
                :alt="$t('tamtam.addGroupAvatar')"
              />
              <f7-badge v-else color="auto" class="groupAvatar__badge">
                <f7-icon
                  ios="f7:person_3_fill"
                  aurora="f7:person_3_fill"
                  md="material:groups"
                ></f7-icon>
              </f7-badge>
            </template>
            <template #title>
              {{ avatarDraft ? $t("tamtammessage.changePhoto") : $t("tamtam.addGroupAvatar") }}
            </template>
          </f7-list-item>
          <f7-list-item v-if="avatarDraft" link="#" @click="discardAvatar">
            <template #media>
              <span class="material-symbols-outlined groupRowIcon groupRowIcon--danger">
                delete
              </span>
            </template>
            <template #title>
              <span class="color-deeporange">{{ $t("tamtam.removeAvatar") }}</span>
            </template>
          </f7-list-item>
        </f7-list>

        <f7-list v-if="selectedMembers.length" dividers-ios strong-ios class="groupSelectedList">
          <f7-list-group>
            <li class="item-content">
              <div class="item-inner">
                <div class="item-title">
                  {{ $t("tamtam.selectedMembers", { count: selectedMembers.length }) }}
                </div>
                <div class="item-footer groupSelectedList__names">
                  {{ selectedMemberNames }}
                </div>
              </div>
            </li>
          </f7-list-group>
        </f7-list>

        <form @submit.prevent="submit">
          <f7-list dividers-ios strong-ios class="groupForm">
            <f7-list-input
              :label="$t('common.name')"
              floating-label
              type="text"
              name="groupName"
              :value="groupName"
              :placeholder="$t('common.name')"
              :required="true"
              :clear-button="true"
              :error="showNameError"
              :error-message="showNameError ? $t('tamtam.groupNameRequired') : ''"
              maxlength="60"
              @input="onNameInput"
            ></f7-list-input>
            <f7-list-input
              :label="$t('common.description')"
              floating-label
              type="text"
              name="description"
              :value="description"
              :placeholder="$t('common.description')"
              :clear-button="true"
              maxlength="160"
              @input="onDescriptionInput"
            ></f7-list-input>
          </f7-list>
        </form>
        <f7-toolbar tabbar bottom no-shadow class="groupFooter">
          <div class="page-content">
            <f7-button
              large
              fill
              class="groupActionBtn"
              :disabled="!canSubmit"
              @click="submit"
            >
              <span
                v-if="isSubmitting"
                class="preloader"
                style="--f7-preloader-size: 20px"
              ></span>
              <template v-else>{{ submitLabel }}</template>
            </f7-button>
          </div>
        </f7-toolbar>
      </f7-page>
    </f7-view>
  </f7-panel>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import { f7 } from "framework7-vue";
import { useMessageStore } from "../../js/pinia-stores/message.js";
import { useAuthStore } from "../../js/pinia-stores/auth.js";
import { useMessageGroup } from "@/js/composables/useMessage.js";
import { useAttachDocs } from "@/js/composables/useAttachDocs.js";
import { useGroupCreation } from "@/js/composables/useGroupCreation.js";
import { getInitial } from "@/js/utils/initial.js";
import i18n from "../../js/i18n.js";

const messageStore = useMessageStore();
const authStore = useAuthStore();
const { documentsUrl } = storeToRefs(authStore);

const {
  parentGroup,
  isSubGroupCreation,
  parentGroupName,
  candidates,
  isLoadingCandidates,
  loadCandidates,
  openDetailsPanel,
  closeGroupCreation,
  resetGroupCreation,
} = useGroupCreation();

const selectedMembers = ref([]);
const groupName = ref("");
const description = ref("");
const isSubmitting = ref(false);
const showNameError = ref(false);
const avatarDraft = ref(null);
let pendingPickIds = null;

const { createGroup, createSubGroup } = useMessageGroup({
  groupName,
  description,
  chatUsers: selectedMembers,
  documentsUrl,
  avatarDocument: avatarDraft,
});
const { pickImages } = useAttachDocs();

const flowTitle = computed(() =>
  isSubGroupCreation.value ? i18n.global.t("tamtam.newSubgroup") : i18n.global.t("tamtam.newGroup")
);
const submitLabel = computed(() =>
  isSubGroupCreation.value ? i18n.global.t("tamtam.createSubgroup") : i18n.global.t("tamtam.createGroup")
);
const selectedMemberNames = computed(() => selectedMembers.value.map((member) => member.name).join(", "));
const canContinue = computed(() => selectedMembers.value.length > 0);
const canSubmit = computed(() => !isSubmitting.value && groupName.value.trim().length > 0);
const avatarPreview = computed(() => avatarDraft.value?.url || "");

function memberKey(member) {
  return member.userId || member.id;
}

function avatarFor(member) {
  return member.avatar || member.logo || "";
}

function initialFor(member) {
  return getInitial(member.name || member.username || "") || "";
}

function isSelected(member) {
  const key = memberKey(member);
  return selectedMembers.value.some((selected) => memberKey(selected) === key);
}

function toggleMember(member) {
  if (isSelected(member)) {
    removeMember(member);
    return;
  }
  selectedMembers.value = [...selectedMembers.value, member];
}

function removeMember(member) {
  const key = memberKey(member);
  selectedMembers.value = selectedMembers.value.filter((selected) => memberKey(selected) !== key);
}

function onNameInput(event) {
  groupName.value = event.target.value;
  if (showNameError.value && groupName.value.trim()) showNameError.value = false;
}

function onDescriptionInput(event) {
  description.value = event.target.value;
}

function onMembersPanelOpened() {
  resetDraft();
  loadCandidates();
}

function onMembersPanelClosed() {
  resetDraft();
  resetGroupCreation();
}

function onDetailsPanelOpened() {
  showNameError.value = false;
}

function goToDetails() {
  if (!canContinue.value) return;
  openDetailsPanel();
}

async function pickAvatar() {
  if (isSubmitting.value) return;
  pendingPickIds = new Set((documentsUrl.value || []).map((doc) => doc.fileId));
  await pickImages();
}

function discardAvatar() {
  const draft = avatarDraft.value;
  avatarDraft.value = null;
  pendingPickIds = null;
  // `fileName` covers the document that was replaced by its uploaded version.
  if (draft) authStore.unSetDocumentsUrl({ fileId: draft.fileId, fileName: draft.fileName });
}

function resetDraft() {
  discardAvatar();
  selectedMembers.value = [];
  groupName.value = "";
  description.value = "";
  isSubmitting.value = false;
  showNameError.value = false;
}

async function submit() {
  if (isSubmitting.value) return;
  const name = groupName.value.trim();
  if (!name) {
    showNameError.value = true;
    return;
  }
  groupName.value = name;
  if (typeof description.value === "string") {
    description.value = description.value.trim();
  }
  isSubmitting.value = true;
  messageStore.error = null;
  try {
    const created = isSubGroupCreation.value
      ? await createSubGroup(parentGroup.value)
      : await createGroup();
    if (!created) throw new Error(messageStore.error || i18n.global.t("common.errorOccurred"));
    closeGroupCreation();
  } catch (err) {
    f7.dialog.alert({
      title: i18n.global.t("common.error"),
      text: err?.message || i18n.global.t("common.errorOccurred"),
    });
  } finally {
    isSubmitting.value = false;
  }
}

watch(
  documentsUrl,
  (docs) => {
    if (!pendingPickIds || !Array.isArray(docs)) return;
    const picked = docs.find((doc) => doc.fileType === "image" && !pendingPickIds.has(doc.fileId));
    if (!picked) return;
    discardAvatar();
    avatarDraft.value = picked;
    pendingPickIds = null;
  },
  { deep: true }
);
</script>

<style scoped>
.groupPanel {
  min-width: 100%;
  border-right: var(--f7-page-master-border-width) solid var(--f7-page-master-border-color);
}

@media (min-width: 768px) {
  .groupPanel {
    min-width: 418px;
  }
}

.groupPage {
  background: var(--avocash-surface, #f4f7f2);
}

.groupPage :deep(.page-content) {
  padding-bottom: calc(var(--f7-tabbar-height) + var(--f7-safe-area-bottom, 0px) + 8px);
}

.groupNavTitle {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.groupNavTitle__main {
  font-size: var(--f7-navbar-title-font-size, 17px);
  font-weight: 700;
  color: var(--f7-navbar-title-color, var(--f7-theme-color));
}

.groupNavTitle__sub {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--f7-on-surface-variant, #8e8e93);
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.groupIconBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.groupTextBtn {
  font-size: var(--f7-font-size-sm, 14px);
  font-weight: 600;
  padding: 0 4px;
}

.groupSelected {
  margin: 12px 12px 4px;
  padding: 10px 12px;
  border-radius: 16px;
  background: var(--f7-list-item-background-color, #fff);
  border: 1px solid var(--f7-page-master-border-color, rgba(0, 0, 0, 0.05));
}

.groupSelected__label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-bottom: 8px;
}

.groupChips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.groupChip {
  margin: 0;
  max-width: 160px;
}

.groupChip__media {
  width: var(--f7-chip-height);
  height: var(--f7-chip-height);
  border-radius: 50%;
  object-fit: cover;
}

.groupMember {
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;
}

.groupMember__media {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
}

.groupMember__badge {
  width: 44px;
  height: 44px;
  font-size: 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.groupState {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
}

.groupAvatarList {
  margin-top: var(--f7-safe-area-top, 0px);
}

.groupAvatar__media {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}

.groupAvatar__badge {
  width: 48px;
  height: 48px;
  font-size: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.groupRowIcon {
  font-size: 18px;
  align-self: center;
}

.groupRowIcon--danger {
  color: var(--f7-color-deeporange, #ff3b30);
}

.groupSelectedList__names {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.groupForm {
  margin-bottom: 0;
}

.groupFooter {
  background: var(--avocash-surface, #f4f7f2);
  box-shadow: none;
}

.groupActionBtn {
  --f7-button-hover-opacity: 0.92;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}
</style>
